import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { API_URL } from '../../constants/string-constants';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--clr-prim);
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
`;

const ModalTitle = styled.h2`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.4rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--clr-sec);
  width: 30px;
  height: 30px;
  border-radius: 50%;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const UserInfo = styled.div`
  background-color: var(--clr-bg);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.25rem;
  border-left: 4px solid #d19a00;
`;

const UserIdText = styled.p`
  margin: 0;
  color: var(--clr-sec);
  font-family: monospace;
  font-size: 0.85rem;
  opacity: 0.8;
  word-break: break-all;
`;

const MessageText = styled.p`
  margin: 0.5rem 0 0 0;
  color: var(--clr-sec);
  font-style: italic;
  word-break: break-word;
`;

const HistoryBox = styled.div`
  background-color: var(--clr-bg);
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.25rem;
  color: var(--clr-sec);
  font-size: 0.9rem;
`;

const HistoryCount = styled.div`
  font-weight: 600;
  color: ${(props) => (props.count > 0 ? '#d19a00' : 'var(--clr-sec)')};
  margin-bottom: ${(props) => (props.count > 0 ? '0.5rem' : '0')};
`;

const HistoryList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  max-height: 120px;
  overflow-y: auto;
`;

const HistoryItem = styled.li`
  margin-bottom: 0.35rem;
  opacity: 0.85;
  font-size: 0.85rem;
`;

const ActiveBanner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  background-color: rgba(209, 154, 0, 0.12);
  border: 1px solid rgba(209, 154, 0, 0.4);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.25rem;
  color: var(--clr-sec);
  font-size: 0.9rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--clr-sec);
  font-weight: 500;
`;

const PresetRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PresetButton = styled.button`
  padding: 0.55rem 1rem;
  border-radius: 20px;
  border: 1px solid ${(props) => (props.selected ? '#d19a00' : 'rgba(255,255,255,0.2)')};
  background-color: ${(props) => (props.selected ? 'rgba(209, 154, 0, 0.18)' : 'transparent')};
  color: var(--clr-sec);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #d19a00;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  resize: vertical;
  font-family: inherit;
  font-size: 0.95rem;
  box-sizing: border-box;
  background-color: var(--clr-bg);
  color: var(--clr-sec);
`;

// Custom duration input (amount + unit), mirroring ModBanModal's
// DurationInputGroup so the two mod modals feel consistent.
const DurationInputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  margin-top: 0.65rem;
`;

const NumberInput = styled.input`
  flex: 1;
  min-width: 80px;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 0.95rem;
  box-sizing: border-box;
  background-color: var(--clr-bg);
  color: var(--clr-sec);

  &:focus {
    outline: none;
    border-color: #d19a00;
  }
`;

const UnitSelect = styled.select`
  flex: 1;
  min-width: 120px;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 0.95rem;
  box-sizing: border-box;
  background-color: var(--clr-bg);
  color: var(--clr-sec);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #d19a00;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.7rem 1.25rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.95rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  color: var(--clr-sec);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const TimeoutButton = styled(Button)`
  background-color: #d19a00;
  color: #fff;

  &:hover:not(:disabled) {
    background-color: #b78600;
  }
`;

const ClearButton = styled(Button)`
  background-color: transparent;
  color: #d19a00;
  border: 1px solid #d19a00;
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;

  &:hover:not(:disabled) {
    background-color: rgba(209, 154, 0, 0.1);
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  background-color: rgba(255, 68, 68, 0.1);
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #2ecc71;
  background-color: rgba(46, 204, 113, 0.1);
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

// Duration presets (minutes). 24h is the server-enforced cap — anything
// harsher should be a ban.
const PRESETS = [
  { label: '5 min', minutes: 5 },
  { label: '15 min', minutes: 15 },
  { label: '1 hour', minutes: 60 },
  { label: '24 hours', minutes: 1440 },
];

// Server-enforced cap (see MAX_TIMEOUT_MINUTES in api/routes/mod.js). The
// custom input is validated against this client-side too.
const MAX_TIMEOUT_MINUTES = 24 * 60;

const convertToMinutes = (amount, unit) => {
  const num = parseInt(amount, 10);
  if (!Number.isFinite(num)) return NaN;
  return unit === 'hours' ? num * 60 : num;
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const describeMinutes = (m) => {
  if (m >= 1440) return `${Math.round(m / 1440)}d`;
  if (m >= 60) return `${Math.round(m / 60)}h`;
  return `${m}m`;
};

const TimeoutModal = ({ isOpen, onClose, message, room = 'general' }) => {
  const [minutes, setMinutes] = useState(15);
  const [customMode, setCustomMode] = useState(false);
  const [customAmount, setCustomAmount] = useState('30');
  const [customUnit, setCustomUnit] = useState('minutes');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = React.useCallback(async () => {
    if (!message?.userId) return;
    setHistoryLoading(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      if (!token) return;
      const res = await fetch(
        `${API_URL}mod/timeouts/${encodeURIComponent(message.userId)}?room=${encodeURIComponent(room)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (err) {
      console.error('Failed to load timeout history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [message?.userId, room]);

  useEffect(() => {
    if (!isOpen || !message?.userId) {
      setHistory(null);
      return;
    }
    loadHistory();
  }, [isOpen, message?.userId, loadHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const effectiveMinutes = customMode
      ? convertToMinutes(customAmount, customUnit)
      : minutes;
    if (!Number.isFinite(effectiveMinutes) || effectiveMinutes < 1) {
      setError('Please enter a valid duration');
      return;
    }
    if (effectiveMinutes > MAX_TIMEOUT_MINUTES) {
      setError('Timeout cannot exceed 24 hours. For longer, use a ban.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}mod/timeout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: message.userId,
          room,
          minutes: effectiveMinutes,
          reason: reason.trim() || undefined,
        }),
      });

      if (response.ok) {
        setSuccess(`User timed out for ${describeMinutes(effectiveMinutes)}`);
        setReason('');
        setTimeout(() => {
          onClose();
          setSuccess('');
        }, 1500);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Failed to time out user');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearTimeout = async () => {
    setClearing(true);
    setError('');
    setSuccess('');
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}mod/clear-timeout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId: message.userId, room }),
      });
      if (response.ok) {
        setSuccess('Active timeout cleared');
        await loadHistory();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Failed to clear timeout');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setCustomMode(false);
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen || !message) return null;

  const timeouts = history?.timeouts || [];
  const activeTimeout = timeouts.find((t) => t.active);
  const count = timeouts.length;

  // What the submit button should say / send.
  const effectiveMinutes = customMode
    ? convertToMinutes(customAmount, customUnit)
    : minutes;
  const submitLabel = Number.isFinite(effectiveMinutes) && effectiveMinutes >= 1
    ? `Timeout ${describeMinutes(effectiveMinutes)}`
    : 'Timeout';

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>⏱️ Timeout user</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <UserInfo>
          <UserIdText>User ID: {message.userId}</UserIdText>
          {message.messageText && (
            <MessageText>"{message.messageText}"</MessageText>
          )}
        </UserInfo>

        {activeTimeout && (
          <ActiveBanner>
            <span>
              Already timed out until {formatDate(activeTimeout.expiresAt)}
            </span>
            <ClearButton
              type="button"
              onClick={handleClearTimeout}
              disabled={clearing}
            >
              {clearing ? 'Clearing…' : 'Clear now'}
            </ClearButton>
          </ActiveBanner>
        )}

        <HistoryBox>
          {historyLoading ? (
            <div>Loading timeout history...</div>
          ) : (
            <>
              <HistoryCount count={count}>Prior timeouts: {count}</HistoryCount>
              {timeouts.length > 0 && (
                <HistoryList>
                  {timeouts.slice(0, 5).map((t) => (
                    <HistoryItem key={t.id}>
                      {formatDate(t.createdAt)} — until {formatDate(t.expiresAt)}
                      {t.clearedAt ? ' (cleared early)' : t.active ? ' (active)' : ''}
                      {t.reason ? ` — ${t.reason}` : ''}
                    </HistoryItem>
                  ))}
                </HistoryList>
              )}
            </>
          )}
        </HistoryBox>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Duration</Label>
            <PresetRow>
              {PRESETS.map((p) => (
                <PresetButton
                  key={p.minutes}
                  type="button"
                  selected={!customMode && minutes === p.minutes}
                  onClick={() => {
                    setCustomMode(false);
                    setMinutes(p.minutes);
                  }}
                  disabled={loading}
                >
                  {p.label}
                </PresetButton>
              ))}
              <PresetButton
                type="button"
                selected={customMode}
                onClick={() => setCustomMode(true)}
                disabled={loading}
              >
                Custom
              </PresetButton>
            </PresetRow>
            {customMode && (
              <DurationInputGroup>
                <NumberInput
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Amount"
                  min="1"
                  max="1440"
                  disabled={loading}
                />
                <UnitSelect
                  value={customUnit}
                  onChange={(e) => setCustomUnit(e.target.value)}
                  disabled={loading}
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                </UnitSelect>
              </DurationInputGroup>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Reason (internal note, optional)</Label>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Private context for other mods reviewing this user later"
              maxLength={1000}
              disabled={loading}
            />
          </FormGroup>

          <ButtonRow>
            <CancelButton type="button" onClick={handleClose} disabled={loading}>
              Cancel
            </CancelButton>
            <TimeoutButton type="submit" disabled={loading}>
              {loading ? 'Timing out…' : submitLabel}
            </TimeoutButton>
          </ButtonRow>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TimeoutModal;
